import { FC, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Pages
import DashboardPage from '@/pages/Dashboard/DashboardPage';
import LoginPage from '@/pages/Auth/LoginPage';
import UsersPage from '@/pages/Users/UsersPage';
import UserDetailPage from '@/pages/Users/UserDetailPage';
import GroupsPage from '@/pages/Groups/GroupsPage';
import GroupDetailPage from '@/pages/Groups/GroupDetailPage';
import CertificatesPage from '@/pages/Certificates/CertificatesPage';
import AgentsPage from '@/pages/Agents/AgentsPage';
import AgentDetailPage from '@/pages/Agents/AgentDetailPage';
import EventsPage from '@/pages/Events/EventsPage';
import EventDetailsPage from '@/pages/Events/EventDetailsPage';
import EventStuckPage from '@/pages/Events/EventStuckPage';
import EventManagementPage from '@/pages/Events/EventManagementPage';
import EventLogsPage from '@/pages/EventLogs/EventLogsPage';
import LoadingShowcasePage from '@/pages/LoadingShowcase/LoadingShowcasePage';

// Test pages (TODO: Remove in production)
import TestHostnamePage from '@/pages/TestHostnamePage';

// Layouts
import Layout from '@/components/Layout/Layout';
import AuthLayout from '@/components/Layout/AuthLayout';

// Protected Route Component
import ProtectedRoute from '@/components/ProtectedRoute/ProtectedRoute';

// Hooks
import { useAuthInit } from '@/hooks';
import { useLanguageActions } from '@/store/languageStore';

// Toast Notifications
import { Toaster } from '@/components/Common/Toast';

/**
 * Main App Component
 * Handles routing and layout structure
 */
const App: FC = () => {
  // Initialize authentication status on app startup
  useAuthInit();
  
  // Initialize language
  const { initializeLanguage } = useLanguageActions();
  
  // Initialize language on app start
  useEffect(() => {
    initializeLanguage();
  }, [initializeLanguage]);

  return (
    <>
      {/* Toast Notifications */}
      <Toaster />
      
      <Routes>
        {/* Public Routes */}
        <Route path="/auth" element={<AuthLayout />}>
          <Route path="login" element={<LoginPage />} />
          <Route index element={<Navigate to="login" replace />} />
        </Route>

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        
        {/* Users Routes */}
        <Route path="users" element={<UsersPage />} />
        <Route path="users/:reference" element={<UserDetailPage />} />

        {/* Groups Routes */}
        <Route path="groups" element={<GroupsPage />} />
        <Route path="groups/:reference" element={<GroupDetailPage />} />

        {/* Agents Routes */}
        <Route path="agents" element={<AgentsPage />} />
        <Route path="agents/:reference" element={<AgentDetailPage />} />

        {/* Certificates Routes */}
        <Route path="certificates" element={<CertificatesPage />} />

        {/* Events Routes */}
        <Route path="events" element={<EventsPage />} />
        <Route path="events/details" element={<EventDetailsPage />} />
        <Route path="events/stuck" element={<EventStuckPage />} />
        <Route path="events/management" element={<EventManagementPage />} />
        
        {/* Event Logs Routes */}
        <Route path="event-logs" element={<EventLogsPage />} />

        {/* Loading Component Showcase */}
        <Route path="showcase/loading" element={<LoadingShowcasePage />} />
        
        {/* Test Routes (TODO: Remove in production) */}
        <Route path="test-hostname" element={<TestHostnamePage />} />
      </Route>

      {/* Fallback Route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
};

export default App;